class DlPageCleansLikeAProLL extends HTMLElement {
    constructor() {
        super();
        this.animationFrameId = null;
        this.unifiedCarouselSwiper = null; // 统一的 Swiper 实例
        this.playVideoTimeout = null; // 存储播放视频的 setTimeout ID
        this.expectedActiveIndex = null; // 期望激活的视频索引
        this.resizeTimeout = null; // 防抖定时器

        // 视口可见性相关属性
        this.intersectionObserver = null;
        this.isContainerVisible = false; // 容器是否可见
        this.wasPausedByVisibility = false; // 是否因为离开视口而暂停

        this.pausedProgressStates = new Map(); // 保存每个进度条暂停时的状态
    }

    // 元素被插入到 DOM 时自动调用
    connectedCallback() {
        this.bindEvents(); // 绑定事件
        this.initActiveState(); // 初始化第一个激活状态
        this.initUnifiedCarouselSwiper(); // 初始化统一的 Swiper
        this.initVideoAutoPlay(); // 初始化视频自动播放
        this.initVideoProgress(); // 初始化视频进度条
        this.playFirstVideo(); // 播放第一个视频

        // 初始化视口可见性监听
        this.initVisibilityObserver();

        // 添加 resize 监听器
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.handleResize);
    }

    // 添加清理方法（当元素从 DOM 移除时调用）
    disconnectedCallback() {
        // 移除 resize 监听器
        window.removeEventListener('resize', this.handleResize);
        
        // 清理定时器
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        
        if (this.playVideoTimeout) {
            clearTimeout(this.playVideoTimeout);
        }
        
        // 销毁 Swiper
        if (this.unifiedCarouselSwiper) {
            this.unifiedCarouselSwiper.destroy(true, true);
            this.unifiedCarouselSwiper = null;
        }

        // 清理Intersection Observer
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }

        // 清理保存的进度状态
        if (this.pausedProgressStates) {
            this.pausedProgressStates.clear();
        }
    }

    // 绑定事件
    bindEvents() {
        this.toggleUnitContent(); // 切换单元内容
    }

    // 初始化视口可见性监听（PC端和移动端都需要）
    initVisibilityObserver() {
        const isMobile = window.innerWidth < 990;
        
        // 根据设备类型选择对应的容器
        const targetContainer = isMobile 
            ? this.querySelector('.dl-page-cleans-like-a-pro-ll-container.dl-hidden-desktop')
            : this.querySelector('.dl-page-cleans-like-a-pro-ll-container.dl-hidden-mobile');
        
        if (!targetContainer) return;

        // 如果已经存在observer，先断开
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
        }

        // 创建 Intersection Observer，threshold 设置为 0.75（即3/4可见）
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // 当容器3/4在视口中时
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.75) {
                        this.isContainerVisible = true;
                        this.handleContainerVisible();
                    } 
                    // 当容器离开3/4视口时
                    else if (this.isContainerVisible) {
                        this.isContainerVisible = false;
                        this.handleContainerHidden();
                    }
                });
            },
            {
                threshold: [0, 0.25, 0.5, 0.75, 1],
                rootMargin: '0px'
            }
        );

        this.intersectionObserver.observe(targetContainer);
    }

    // 处理容器进入视口（3/4可见）- 支持PC和移动端
    handleContainerVisible() {
        const isMobile = window.innerWidth < 990;
        
        // 如果之前因为离开视口而暂停，则继续播放
        if (isMobile && this.unifiedCarouselSwiper) {
            const activeSlide = this.unifiedCarouselSwiper.slides[this.unifiedCarouselSwiper.activeIndex];
            
            if (activeSlide && !activeSlide.classList.contains('swiper-slide-duplicate')) {
                const videoElement = activeSlide.querySelector('video');
                
                if (videoElement && videoElement.paused) {
                    videoElement.play().catch(err => {
                        console.warn('Resume video playback failed:', err);
                    });
                }
            }
        }
        else {
            // PC端逻辑：找到当前激活的视频
            const activeVideoItem = this.querySelector('.dl-hidden-mobile .dl-video-item.active');
            
            if (activeVideoItem) {
                const videoElement = activeVideoItem.querySelector('video');
                
                if (videoElement && videoElement.paused) {
                    videoElement.play().catch(err => {
                        console.warn('Resume PC video playback failed:', err);
                    });
                }
            }
        }

        // 重置标志
        this.wasPausedByVisibility = false;
    }

    // 处理容器离开视口 - 支持PC和移动端
    handleContainerHidden() {
        const isMobile = window.innerWidth < 990;
        
        if (isMobile && this.unifiedCarouselSwiper) {
            const activeSlide = this.unifiedCarouselSwiper.slides[this.unifiedCarouselSwiper.activeIndex];
            
            if (activeSlide && !activeSlide.classList.contains('swiper-slide-duplicate')) {
                const videoElement = activeSlide.querySelector('video');
                
                if (videoElement && !videoElement.paused) {
                    videoElement.pause();
                    this.wasPausedByVisibility = true;
                }
            }
        }
        else {
            // PC端逻辑：找到当前激活的视频
            const activeVideoItem = this.querySelector('.dl-hidden-mobile .dl-video-item.active');
            
            if (activeVideoItem) {
                const videoElement = activeVideoItem.querySelector('video');
                
                if (videoElement && !videoElement.paused) {
                    videoElement.pause();
                    this.wasPausedByVisibility = true;
                }
            }
        }
    }

    // 更新白色滚动条高度
    updateWhiteScroll() {
        const whiteScroll = this.querySelector('.dl-page-cleans-like-a-pro-ll-scroll svg:nth-child(1)');
        const textContentList = this.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-list');
        
        if (whiteScroll && textContentList) {
            whiteScroll.style.height = textContentList.offsetHeight + 'px';
        }
    }


    // 更新绿色滚动条位置和高度
    updateGreenScroll(activeDescription) {
        const greenScroll = this.querySelector('.dl-page-cleans-like-a-pro-ll-scroll svg:nth-child(2)');
        const textContentList = this.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-list');
        
        if (!greenScroll || !textContentList || !activeDescription) return;
        
        // 获取激活描述所在的单元（包含标题+描述）
        const activeUnit = activeDescription.closest('.dl-page-cleans-like-a-pro-ll-text-content-unit');
        
        // 计算该单元相对于“文本内容列表”的顶部位置（绿色条的起始位置）
        const unitOffsetTop = activeUnit.offsetTop;
        
        // 计算该单元的总高度（标题高度 + 描述高度）
        const unitHeight = activeUnit.offsetHeight;

        // 设置绿色滚动条的位置和高度
        greenScroll.style.top = unitOffsetTop + 'px';
        greenScroll.style.height = unitHeight + 'px';
    }

    // 计算并设置绿色滚动条的最终位置（假设所有其他描述都收起）
    updateGreenScrollToFinalPosition(targetDescription) {
        const greenScroll = this.querySelector('.dl-page-cleans-like-a-pro-ll-scroll svg:nth-child(2)');
        const textContentList = this.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-list');
        
        if (!greenScroll || !textContentList || !targetDescription) return;
        
        const targetUnit = targetDescription.closest('.dl-page-cleans-like-a-pro-ll-text-content-unit');
        const allUnits = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-text-content-unit');

        // 获取 gap 值（只需要计算一次）
        const computedStyle = window.getComputedStyle(textContentList);
        const gap = parseFloat(computedStyle.gap) || 0;
        
        // 计算目标单元之前的所有单元的标题高度之和
        let finalTopPosition = 0;
        let foundTarget = false;
        
        for (const unit of allUnits) {
            if (unit === targetUnit) {
                foundTarget = true;
                break; // 找到目标单元，停止计算
            }
            
            const title = unit.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-title');
            if (title) {
                finalTopPosition += title.offsetHeight;
            }
            
            // 在每个单元后面加上 gap
            finalTopPosition += gap;
        }
        
        // 计算目标单元的最终高度（标题 + 展开的描述）
        const targetTitle = targetUnit.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-title');
        const titleHeight = targetTitle ? targetTitle.offsetHeight : 0;
        const descriptionHeight = targetDescription.scrollHeight;

        // 手动添加 description.active 的 margin-top（在 1920px 下是 10px）
        // 在响应式下，这个值是 calc(10 / 1920 * 100vw)
        const marginTop = window.innerWidth >= 990 ? (10 / 1920 * window.innerWidth) : 0;

        const finalHeight = titleHeight + descriptionHeight + marginTop;
        
        // 设置绿色滚动条的最终位置和高度
        greenScroll.style.top = finalTopPosition + 'px';
        greenScroll.style.height = finalHeight + 'px';
    }


    // 初始化第一个激活状态
    initActiveState() {
        const activeDescription = this.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-description.active');
        
        if (activeDescription) {
            // 临时禁用 transition，避免初始化时的动画
            activeDescription.style.transition = 'none';

            // 立即设置高度（无动画）
            activeDescription.style.maxHeight = activeDescription.scrollHeight + 'px';

            // 强制浏览器重排
            void activeDescription.offsetHeight;

            //恢复 transition，后续点击其他标题时有动画效果
            activeDescription.style.transition = '';

            // 立即更新白色滚动条高度
            this.updateWhiteScroll();

            // 更新绿色滚动条的位置和高度
            this.updateGreenScroll(activeDescription);
        }
    }


    // 播放第一个视频 - PC和移动端都考虑可见性
    playFirstVideo() {
        // PC端
        const firstVideo = this.querySelector('.dl-hidden-mobile .dl-video-item.active video');
        if (firstVideo) {
            const tryPlay = () => {
                // PC端也需要检查容器可见性
                if (window.innerWidth >= 990) {
                    setTimeout(() => {
                        // 默认假设初始时容器是可见的
                        if (this.isContainerVisible !== false) {
                            firstVideo.play().catch(err => {
                                console.warn('First video playback failed:', err);
                            });
                        }
                    }, 100);
                } else {
                    firstVideo.play().catch(err => {
                        console.warn('First video playback failed:', err);
                    });
                }
            };

            if (firstVideo.readyState >= 2) {
                tryPlay();
            } else {
                firstVideo.addEventListener('loadedmetadata', tryPlay, { once: true });
            }
        }

        // 移动端：使用 Swiper API 获取当前激活的 slide
        if (this.unifiedCarouselSwiper) {
            this.expectedActiveIndex = this.unifiedCarouselSwiper.realIndex;
            
            const activeSlide = this.unifiedCarouselSwiper.slides[this.unifiedCarouselSwiper.activeIndex];
            
            if (activeSlide && !activeSlide.classList.contains('swiper-slide-duplicate')) {
                const firstVideoMB = activeSlide.querySelector('video');
                
                if (firstVideoMB) {
                    const tryPlay = () => {
                        if (window.innerWidth < 990) {
                            setTimeout(() => {
                                if (this.isContainerVisible !== false) {
                                    firstVideoMB.play().catch(err => {
                                        console.warn('First mobile video playback failed:', err);
                                    });
                                }
                            }, 100);
                        } else {
                            firstVideoMB.play().catch(err => {
                                console.warn('First mobile video playback failed:', err);
                            });
                        }
                    };
            
                    if (firstVideoMB.readyState >= 2) {
                        tryPlay();
                    } else {
                        firstVideoMB.addEventListener('loadedmetadata', tryPlay, { once: true });
                    }
                }
            }
        }
    }


    // 处理窗口大小变化 - 切换PC/移动端时重新初始化observer
    handleResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        
        this.resizeTimeout = setTimeout(() => {
            const isMobile = window.innerWidth < 990;
            
            if (isMobile && !this.unifiedCarouselSwiper) {
                // 从PC切换到移动端
                this.initUnifiedCarouselSwiper();
                // 重新初始化视口监听（会自动切换到移动端容器）
                this.initVisibilityObserver();
                this.playFirstVideo();
            } 
            else if (!isMobile && this.unifiedCarouselSwiper) {
                // 从移动端切换到PC端
                this.unifiedCarouselSwiper.destroy(true, true);
                this.unifiedCarouselSwiper = null;
                this.expectedActiveIndex = null;
                
                // 重新初始化视口监听（会自动切换到PC端容器）
                this.isContainerVisible = false;
                this.wasPausedByVisibility = false;
                this.initVisibilityObserver();
                
                setTimeout(() => {
                    if (window.innerWidth < 990) return;

                    // 手动检查PC端容器是否在视口中
                    const pcContainer = this.querySelector('.dl-page-cleans-like-a-pro-ll-container.dl-hidden-mobile');
                    if (pcContainer) {
                        const rect = pcContainer.getBoundingClientRect();
                        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
                        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
                        
                        // 计算容器的可见比例
                        const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
                        const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
                        const visibleArea = Math.max(0, visibleHeight) * Math.max(0, visibleWidth);
                        const totalArea = rect.height * rect.width;
                        const visibleRatio = totalArea > 0 ? visibleArea / totalArea : 0;
                        
                        // 只有当容器75%以上可见时，才标记为可见
                        this.isContainerVisible = visibleRatio >= 0.75;
                    }
                    
                    requestAnimationFrame(() => {
                        this.updateWhiteScroll();
                        
                        const activeDescription = this.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-description.active');
                        if (activeDescription) {
                            this.updateGreenScroll(activeDescription);
                        }
                    });

                    // 重新播放PC端视频
                    this.playFirstVideo();
                }, 50);
            } 
            else if (!isMobile) {
                // PC端尺寸变化
                if (window.innerWidth < 990) return;
                
                requestAnimationFrame(() => {
                    this.updateWhiteScroll();
                    
                    const activeDescription = this.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-description.active');
                    if (activeDescription) {
                        this.updateGreenScroll(activeDescription);
                    }
                });
            }
        }, 200);
    }

    // 初始化统一的轮播 Swiper
    initUnifiedCarouselSwiper() {
        const carouselContainer = this.querySelector('.dl-page-cleans-like-a-pro-ll-unified-carousel');
        
        if (!carouselContainer) return;

        // 如果已经初始化过，先销毁
        if (this.unifiedCarouselSwiper) {
            this.unifiedCarouselSwiper.destroy(true, true);
            this.unifiedCarouselSwiper = null;
        }
        
        if (window.innerWidth < 990) {
            if (typeof Swiper !== 'undefined') {
                const self = this;
                
                this.unifiedCarouselSwiper = new Swiper(carouselContainer, {
                    direction: 'horizontal',
                    loop: true,
                    slidesPerView: 1,
                    spaceBetween: 0,
                    speed: 500,
                    allowTouchMove: true,
                    simulateTouch: true,
                    
                    on: {
                        // Swiper 初始化完成后立即同步状态
                        init: function() {
                            setTimeout(() => {
                                self.handleSlideChange(this.realIndex);
                            }, 50);
                        },
                        
                        // 使用 slideChangeTransitionEnd 确保索引已稳定
                        slideChangeTransitionEnd: function () {
                            self.handleSlideChange(this.realIndex);
                        }
                    }
                });
            }
        }
    }

    // 处理 slide 切换（需要考虑可见性）
    handleSlideChange(blockIndex) {
        if (!this.unifiedCarouselSwiper) return;
        
        const targetIndex = blockIndex;
        this.expectedActiveIndex = targetIndex;
        
        if (this.playVideoTimeout) {
            clearTimeout(this.playVideoTimeout);
            this.playVideoTimeout = null;
        }
        
        const targetSlide = Array.from(
            this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')
        ).find(slide => {
            return parseInt(slide.getAttribute('data-block-index')) === targetIndex;
        });
        
        if (!targetSlide) return;
        
        const videoElement = targetSlide.querySelector('video');
        
        // 先暂停所有视频
        const allOriginalVideos = this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate) video');
        allOriginalVideos.forEach((video) => {
            video.pause();
            video.currentTime = 0;
        });

        // 重置所有进度条的宽度
        const allProgressBars = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-progress-bar-item');
        allProgressBars.forEach((progressBar) => {
            const fill = progressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
            if (fill) {
                fill.style.transform = 'scaleX(0)'; 
            }
        });
        
        // 延迟播放目标视频
        this.playVideoTimeout = setTimeout(() => {
            if (this.unifiedCarouselSwiper && this.unifiedCarouselSwiper.realIndex === targetIndex) {
                if (videoElement) {
                    videoElement.currentTime = 0;
                    
                    // 只在容器可见时播放
                    if (this.isContainerVisible) {
                        videoElement.play().catch(err => {
                            console.warn('Video playback failed:', err);
                        });
                    } else {
                        // 如果容器不可见，标记为因可见性而暂停
                        this.wasPausedByVisibility = true;
                    }
                }
            }
            this.playVideoTimeout = null;
        }, 10);
    }


    // 从 Swiper 滑动触发的切换
    switchToBlockFromSwiper(blockIndex) {
        // 切换视频（不再调用 Swiper，避免循环）
        this.switchVideoOnly(blockIndex.toString());
        
        // 切换文本描述（PC端）
        const allUnits = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-text-content-unit');
        const targetUnit = Array.from(allUnits).find(unit => 
            unit.getAttribute('data-block-index') === blockIndex.toString()
        );
        
        if (targetUnit) {
            const targetDescription = targetUnit.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-description');
            if (targetDescription) {
                this.switchTextDescription(targetDescription);
            }
        }
    }

    // 更新进度条的 active 状态
    updateProgressBarActiveState(blockIndex) {
        const allProgressBars = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-progress-bar-item');
        
        allProgressBars.forEach((progressBar) => {
            const background = progressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-background');
            const fill = progressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
            const barIndex = progressBar.getAttribute('data-block-index');
            
            if (barIndex === blockIndex) {
                // 激活状态
                if (background) background.classList.add('active');
                if (fill) fill.classList.add('active');
            } 
            else {
                // 非激活状态
                if (background) background.classList.remove('active');
                if (fill) fill.classList.remove('active');
            }
        });
    }

    // 只切换视频，不触发 Swiper
    switchVideoOnly(blockIndex) {
        const allVideos = this.querySelectorAll('.dl-video-item');
        const allProgressBars = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-progress-bar-item');
        
        allVideos.forEach((videoItem) => {
            const videoElement = videoItem.querySelector('video');
            const videoIndex = videoItem.getAttribute('data-block-index');

            if (videoIndex === blockIndex) {
                videoItem.classList.add('active');
                if (videoElement) {
                    videoElement.currentTime = 0;
                    
                    // PC端：检查容器可见性
                    if (this.isContainerVisible) {
                        videoElement.play();
                    } else {
                        // 如果容器不可见，标记为因可见性而暂停
                        this.wasPausedByVisibility = true;
                    }
                    
                    // 重置对应的进度条
                    const targetProgressBar = Array.from(allProgressBars).find(
                        bar => bar.getAttribute('data-block-index') === blockIndex
                    );
                    if (targetProgressBar) {
                        const fill = targetProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                        if (fill) fill.style.width = '0%';
                    }
                }
            } 
            else {
                videoItem.classList.remove('active');
                if (videoElement) {
                    videoElement.pause();
                    videoElement.currentTime = 0;
                }

                const inactiveProgressBar = Array.from(allProgressBars).find(
                    bar => bar.getAttribute('data-block-index') === videoIndex
                );
                if (inactiveProgressBar) {
                    const fill = inactiveProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                    if (fill) fill.style.width = '0%';
                }
            }
        });
    }


    // 切换视频
    switchVideo(blockIndex) {
        const allVideos = this.querySelectorAll('.dl-video-item');
        const allProgressBars = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-progress-bar-item');
        
        allVideos.forEach((videoItem) => {
            const videoElement = videoItem.querySelector('video');
            const videoIndex = videoItem.getAttribute('data-block-index');

            if (videoIndex === blockIndex) {
                videoItem.classList.add('active');
                if (videoElement) {
                    videoElement.currentTime = 0;
                    
                    // PC端：检查容器可见性
                    if (this.isContainerVisible) {
                        videoElement.play();
                    } else {
                        // 如果容器不可见，标记为因可见性而暂停
                        this.wasPausedByVisibility = true;
                    }
                    
                    const targetProgressBar = Array.from(allProgressBars).find(
                        bar => bar.getAttribute('data-block-index') === blockIndex
                    );
                    if (targetProgressBar) {
                        const fill = targetProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                        if (fill) fill.style.width = '0%';
                    }
                }
            } 
            else {
                videoItem.classList.remove('active');
                if (videoElement) {
                    videoElement.pause();
                    videoElement.currentTime = 0;
                }

                const inactiveProgressBar = Array.from(allProgressBars).find(
                    bar => bar.getAttribute('data-block-index') === videoIndex
                );
                if (inactiveProgressBar) {
                    const fill = inactiveProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                    if (fill) fill.style.width = '0%';
                }
            }
        });
    }


    // 切换文本描述
    switchTextDescription(targetDescription) {
        // 如果已经是激活状态，不需要切换
        if (targetDescription.classList.contains('active')) {
            return;
        }

        // 获取所有单元
        const allUnits = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-text-content-unit');

        // 计算并设置绿色滚动条的最终位置
        this.updateGreenScrollToFinalPosition(targetDescription);
        
        // 收起所有描述
        allUnits.forEach((unit) => {
            const itemDescription = unit.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-description');
            itemDescription.style.maxHeight = '0';
            itemDescription.classList.remove('active');
        });

        // 强制浏览器重排
        void targetDescription.offsetHeight;

        // 展开目标描述
        targetDescription.classList.add('active');
        targetDescription.style.maxHeight = targetDescription.scrollHeight + 'px';

        // 取消之前的动画帧
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // 只在动画结束时更新一次白色滚动条
        const handleTransitionEnd = (e) => {
            if (e.propertyName === 'max-height') {
                this.updateWhiteScroll();
                // 确保最终位置准确
                this.updateGreenScroll(targetDescription);
                targetDescription.removeEventListener('transitionend', handleTransitionEnd);
            }
        };

        targetDescription.addEventListener('transitionend', handleTransitionEnd);
    }


    // 切换到指定的 block（视频+文本）
    switchToBlock(blockIndex) {
        // 切换视频
        this.switchVideo(blockIndex.toString());
        
        // 切换文本描述
        const allUnits = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-text-content-unit');
        const targetUnit = Array.from(allUnits).find(unit => 
            unit.getAttribute('data-block-index') === blockIndex.toString()
        );
        
        if (targetUnit) {
            const targetDescription = targetUnit.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-description');
            
            this.switchTextDescription(targetDescription);
        }
    }


    // 初始化视频自动播放功能
    initVideoAutoPlay() {
        const pcVideos = this.querySelectorAll('.dl-hidden-mobile .dl-video-item video');
        pcVideos.forEach((video) => {
            // 监听视频播放结束事件
            video.addEventListener('ended', () => {
                // 获取当前视频的容器
                const currentVideoItem = video.closest('.dl-video-item');
                const currentIndex = parseInt(currentVideoItem.getAttribute('data-block-index'));
                
                // 获取所有的文本单元来确定总数
                const allUnits = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-text-content-unit');
                const totalBlocks = allUnits.length;
                
                // 计算下一个视频的索引
                const nextIndex = (currentIndex + 1) % totalBlocks;
                
                // 同时切换视频和文本
                this.switchToBlock(nextIndex);
            });
        });

        // 移动端：只监听原始 slide 中的视频（排除克隆的）
        const mobileVideos = this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate) video');
        
        mobileVideos.forEach((video) => {
            video.addEventListener('ended', () => {
                // 确保 Swiper 已初始化
                if (!this.unifiedCarouselSwiper) return;
                
                // 获取当前视频所在的 slide
                const currentSlide = video.closest('.dl-page-cleans-like-a-pro-ll-slide');
                const currentIndex = parseInt(currentSlide.getAttribute('data-block-index'));
                
                // 再次确认这个视频确实在当前激活的 slide 中
                const activeIndex = this.unifiedCarouselSwiper.realIndex;
                if (activeIndex !== currentIndex) {
                    // 不是当前激活的视频，忽略
                    return;
                }
                
                // 先将进度条设置为 100%
                const allProgressBars = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-progress-bar-item');
                const targetProgressBar = Array.from(allProgressBars).find(
                    bar => bar.getAttribute('data-block-index') === currentIndex.toString()
                );
                
                if (targetProgressBar) {
                    const progressBarFill = targetProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                    if (progressBarFill) {
                        // 使用 transform 完成填充
                        progressBarFill.style.transform = 'scaleX(1)';
                    }
                }

                // 同时在切换开始前就更新下一个进度条的激活状态
                if (this.unifiedCarouselSwiper) {
                    const allSlides = this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)');
                    const totalBlocks = allSlides.length;
                    const nextIndex = (currentIndex + 1) % totalBlocks;
                    
                    // 立即更新下一个进度条的激活状态（在动画开始前）
                    this.updateProgressBarActiveState(nextIndex.toString());
                    
                    // 立即开始切换动画
                    this.unifiedCarouselSwiper.slideToLoop(nextIndex, 500);
                }
            });
        });
    }


    // 切换单元内容
    toggleUnitContent() {
        const unitList = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-text-content-unit');
        
        unitList.forEach((unit) => {
            // 找到每个单元的标题
            const unitTitle = unit.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-title');

            // 找到每个单元的描述
            const unitDescription = unit.querySelector('.dl-page-cleans-like-a-pro-ll-text-content-unit-description');

            // 为每一个单元标题绑定点击事件
            unitTitle.addEventListener('click', () => {
                // 判断当前单元是否有 active 类名
                const isActive = unitDescription.classList.contains('active');

                // 如果当前单元有 active 类名，则不进行任何操作
                if (isActive) {
                    return;
                }

                // 获取当前单元的索引
                const currentIndex = unit.getAttribute('data-block-index');

                // 切换视频显示
                this.switchVideo(currentIndex);
                
                // 切换文本描述
                this.switchTextDescription(unitDescription);
            });
        });
    }

    // 初始化视频进度条
    initVideoProgress() {
        const allProgressBars = this.querySelectorAll('.dl-page-cleans-like-a-pro-ll-progress-bar-item');
        
        if (!allProgressBars || allProgressBars.length === 0) return;
        
        // 只监听原始的 slide 中的视频（不包括克隆的）
        const mobileVideos = this.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate) video');
        
        mobileVideos.forEach((video) => {
            let animationFrameId = null;

            const updateProgress = () => {
                const videoSlide = video.closest('.swiper-slide');
                
                // 确保 Swiper 已初始化且 slide 不是克隆的
                if (!this.unifiedCarouselSwiper || videoSlide.classList.contains('swiper-slide-duplicate')) {
                    return;
                }
            
                const slideIndex = parseInt(videoSlide.getAttribute('data-block-index'));
                
                // 关键修改：使用 expectedActiveIndex 而不是 realIndex
                if (this.expectedActiveIndex === slideIndex && !video.paused && !video.ended) {
                    const blockIndex = videoSlide.getAttribute('data-block-index');
                    const targetProgressBar = Array.from(allProgressBars).find(
                        bar => bar.getAttribute('data-block-index') === blockIndex
                    );
                    
                    if (targetProgressBar) {
                        const progressBarFill = targetProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                        
                        if (progressBarFill && video.duration && video.duration > 0) {
                            const progress = (video.currentTime / video.duration) * 100;

                            // 使用 transform 代替 width 实现更流畅的动画
                            progressBarFill.style.transform = `scaleX(${Math.min(progress / 100, 1)})`;
                            progressBarFill.style.transformOrigin = 'left';
                            progressBarFill.style.width = '100%'; // 设置为100%，用 transform 控制显示
                        }
                    }
                    
                    animationFrameId = requestAnimationFrame(updateProgress);
                }
                else {
                    // 如果不是期望激活的视频，停止更新
                    if (animationFrameId) {
                        cancelAnimationFrame(animationFrameId);
                        animationFrameId = null;
                    }
                }
            };
            
            // 监听视频开始播放
            video.addEventListener('play', () => {
                if (!this.unifiedCarouselSwiper) return;
                
                const videoSlide = video.closest('.swiper-slide');
                if (videoSlide && !videoSlide.classList.contains('swiper-slide-duplicate')) {
                    const slideIndex = parseInt(videoSlide.getAttribute('data-block-index'));
                    
                    // 只有当视频索引匹配期望激活的索引时才更新进度条
                    if (this.expectedActiveIndex === slideIndex) {
                        const blockIndex = videoSlide.getAttribute('data-block-index');
                        const targetProgressBar = Array.from(allProgressBars).find(
                            bar => bar.getAttribute('data-block-index') === blockIndex
                        );
                        
                        if (targetProgressBar) {
                            const progressBarFill = targetProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                            if (progressBarFill) {
                                progressBarFill.style.transform = 'scaleX(0)';
                            }
                        }
                        
                        // 更新进度条激活状态
                        this.updateProgressBarActiveState(blockIndex);
                        
                        if (animationFrameId) {
                            cancelAnimationFrame(animationFrameId);
                            animationFrameId = null;
                        }
                        // 启动进度条更新循环
                        updateProgress();
                    }
                }
            });
            
            // 监听视频结束
            video.addEventListener('ended', () => {
                if (!this.unifiedCarouselSwiper) return;
                
                const videoSlide = video.closest('.swiper-slide');
                if (videoSlide && !videoSlide.classList.contains('swiper-slide-duplicate')) {
                    const slideIndex = parseInt(videoSlide.getAttribute('data-block-index'));
                    
                    // 使用 expectedActiveIndex
                    if (this.expectedActiveIndex === slideIndex) {
                        const blockIndex = videoSlide.getAttribute('data-block-index');
                        const targetProgressBar = Array.from(allProgressBars).find(
                            bar => bar.getAttribute('data-block-index') === blockIndex
                        );
                        
                        if (targetProgressBar) {
                            const progressBarFill = targetProgressBar.querySelector('.dl-page-cleans-like-a-pro-ll-progress-bar-fill');
                            if (progressBarFill) {
                                progressBarFill.style.width = '100%';
                            }
                        }
                    }
                }
                
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
            });
            
            // 监听视频暂停
            video.addEventListener('pause', () => {
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                    animationFrameId = null;
                }
            });
        });
    }

    // 更新视频进度条
    updateVideoProgress(video, progressBarFill) {
        if (video.duration && video.duration > 0) {
            // 计算播放进度百分比
            const progress = (video.currentTime / video.duration) * 100;
            // 更新进度条宽度
            progressBarFill.style.width = `${progress}%`;
        }
    }
}

customElements.define('dl-page-cleans-like-a-pro-ll', DlPageCleansLikeAProLL);